import { LegalBottom } from "@/components/LegalFooter";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import NotFoundPage from "@/pages/NotFoundPage";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Car,
  Bike,
  HeartPulse,
  Shield,
  HardHat,
  Home,
  ShieldCheck,
  Landmark,
  PiggyBank,
  TrendingUp,
  ArrowRight,
  ArrowLeft,
  Check,
  Phone,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

const insuranceIcons = {
  auto: Car,
  moto: Bike,
  sante: HeartPulse,
  prevoyance: Shield,
  rc_decennale: HardHat,
  multirisque: Home,
  cybersecurite: ShieldCheck,
  pret_immobilier: Landmark,
  vie: PiggyBank,
  retraite: TrendingUp,
};

const productImages = {
  auto: "https://images.unsplash.com/photo-1769641156949-c3a75ce2498a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  moto: "https://images.unsplash.com/photo-1763568088984-e6a507ab4219?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  sante: "https://images.unsplash.com/photo-1659353887804-fc7f9313021a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  prevoyance: "https://images.unsplash.com/photo-1690574467268-e77dcaefb77c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  rc_decennale: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  multirisque: "https://images.unsplash.com/photo-1766603636562-531bb3e1dda8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  cybersecurite: "https://images.unsplash.com/photo-1684488624316-774ea1824d97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  pret_immobilier: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  vie: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  retraite: "https://images.unsplash.com/photo-1690574467268-e77dcaefb77c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

const productDetails = {
  auto: {
    title: "Assurance Auto",
    subtitle: "Protection complète pour votre véhicule",
    description: "Notre assurance auto s'adapte à vos besoins avec des formules flexibles allant de la responsabilité civile aux garanties tous risques. Profitez d'une couverture optimale et d'une assistance 24h/24.",
    coverages: [
      { name: "Responsabilité Civile", description: "Obligatoire, couvre les dommages causés aux tiers" },
      { name: "Vol & Incendie", description: "Protection contre le vol et les dégâts liés au feu" },
      { name: "Bris de Glace", description: "Remplacement des vitres et pare-brise" },
      { name: "Tous Risques", description: "Couverture maximale incluant vos propres dommages" },
      { name: "Assistance 0km", description: "Dépannage et remorquage dès le premier kilomètre" },
      { name: "Protection Juridique", description: "Défense de vos intérêts en cas de litige" },
    ],
  },
  moto: {
    title: "Assurance Moto",
    subtitle: "Roulez en toute sérénité",
    description: "Une assurance pensée pour les motards avec des garanties spécifiques pour votre deux-roues et vos équipements. Assistance dédiée et couverture adaptée à votre pratique.",
    coverages: [
      { name: "Responsabilité Civile", description: "Protection des tiers en cas d'accident" },
      { name: "Vol & Tentative", description: "Indemnisation en cas de vol de votre moto" },
      { name: "Équipements Pilote", description: "Couverture casque, blouson, gants..." },
      { name: "Accessoires", description: "Protection des accessoires et modifications" },
      { name: "Assistance 24/7", description: "Dépannage et rapatriement partout en France" },
    ],
  },
  sante: {
    title: "Assurance Santé",
    subtitle: "Votre santé, notre priorité",
    description: "Une complémentaire santé complète qui prend en charge vos frais médicaux au-delà de la Sécurité sociale. Optique, dentaire, hospitalisation : choisissez votre niveau de couverture.",
    coverages: [
      { name: "Hospitalisation", description: "Chambre particulière et frais de séjour" },
      { name: "Soins Courants", description: "Consultations, médicaments, analyses" },
      { name: "Optique Renforcé", description: "Montures et verres progressifs" },
      { name: "Dentaire", description: "Soins, prothèses et orthodontie" },
      { name: "Médecines Douces", description: "Ostéopathie, acupuncture, naturopathie" },
      { name: "Tiers Payant", description: "Pas d'avance de frais chez nos partenaires" },
    ],
  },
  prevoyance: {
    title: "Prévoyance",
    subtitle: "Protégez vos proches",
    description: "Assurez l'avenir de votre famille en cas d'accident de la vie. Capital décès, rente éducation, invalidité : une protection complète pour faire face aux imprévus.",
    coverages: [
      { name: "Capital Décès", description: "Versement d'un capital à vos bénéficiaires" },
      { name: "Rente Éducation", description: "Financement des études de vos enfants" },
      { name: "Invalidité", description: "Revenus de remplacement en cas d'invalidité" },
      { name: "Incapacité", description: "Maintien de salaire en arrêt maladie" },
      { name: "Dépendance", description: "Aide financière en cas de perte d'autonomie" },
    ],
  },
  rc_decennale: {
    title: "RC Décennale",
    subtitle: "Professionnels du bâtiment",
    description: "Obligatoire pour les professionnels de la construction, notre garantie décennale couvre les dommages compromettant la solidité de l'ouvrage pendant 10 ans.",
    coverages: [
      { name: "Garantie Décennale", description: "Couverture des vices de construction pendant 10 ans" },
      { name: "Responsabilité Civile Pro", description: "Dommages causés aux tiers" },
      { name: "Dommages-Ouvrage", description: "Réparation rapide sans recherche de responsabilité" },
      { name: "Protection Juridique", description: "Défense en cas de litige" },
      { name: "Tous Corps d'État", description: "Couverture multi-activités" },
    ],
  },
  multirisque: {
    title: "Multirisque Habitation",
    subtitle: "Protégez votre logement",
    description: "Une assurance habitation complète pour votre maison ou appartement. Incendie, dégât des eaux, vol : dormez sur vos deux oreilles.",
    coverages: [
      { name: "Incendie & Explosion", description: "Reconstruction et remplacement du contenu" },
      { name: "Dégât des Eaux", description: "Fuites, infiltrations, inondations" },
      { name: "Vol & Vandalisme", description: "Effraction, agression, dégradations" },
      { name: "Responsabilité Civile", description: "Dommages causés aux voisins et tiers" },
      { name: "Catastrophes Naturelles", description: "Tempête, grêle, inondation" },
      { name: "Assistance Domicile", description: "Dépannage plomberie, électricité, serrurerie" },
    ],
  },
  cybersecurite: {
    title: "Assurance Cyber-sécurité",
    subtitle: "Protection numérique entreprise",
    description: "Protégez votre entreprise contre les risques numériques : cyber-attaques, ransomware, fuite de données. Une assurance indispensable à l'ère du digital.",
    coverages: [
      { name: "Cyber-attaques", description: "Indemnisation des pertes liées aux intrusions" },
      { name: "Ransomware", description: "Gestion de crise et négociation" },
      { name: "Fuite de Données", description: "Notification RGPD et gestion de la réputation" },
      { name: "Perte d'Exploitation", description: "Compensation du manque à gagner" },
      { name: "E-réputation", description: "Surveillance et nettoyage web" },
      { name: "Assistance 24/7", description: "Hotline experts en cybersécurité" },
    ],
  },
  pret_immobilier: {
    title: "Assurance Prêt Immobilier",
    subtitle: "Sécurisez votre emprunt",
    description: "L'assurance emprunteur qui protège votre crédit immobilier et votre famille. Décès, invalidité, incapacité : des garanties adaptées à votre situation.",
    coverages: [
      { name: "Décès", description: "Remboursement du capital restant dû" },
      { name: "PTIA", description: "Perte Totale et Irréversible d'Autonomie" },
      { name: "ITT", description: "Incapacité Temporaire Totale de travail" },
      { name: "IPT", description: "Invalidité Permanente Totale" },
      { name: "IPP", description: "Invalidité Permanente Partielle" },
      { name: "Perte d'Emploi", description: "Prise en charge des mensualités" },
    ],
  },
  vie: {
    title: "Assurance Vie",
    subtitle: "Épargne et transmission",
    description: "Constituez votre épargne et préparez la transmission de votre patrimoine dans un cadre fiscal avantageux. Fonds euros et unités de compte pour diversifier.",
    coverages: [
      { name: "Fonds Euros", description: "Capital garanti et rendement sécurisé" },
      { name: "Unités de Compte", description: "Diversification sur les marchés financiers" },
      { name: "Gestion Pilotée", description: "Experts qui gèrent pour vous" },
      { name: "Arbitrages Gratuits", description: "Réallocation sans frais" },
      { name: "Fiscalité Avantageuse", description: "Exonérations après 8 ans" },
      { name: "Transmission", description: "Abattements successoraux renforcés" },
    ],
  },
  retraite: {
    title: "Épargne Retraite",
    subtitle: "Préparez votre avenir",
    description: "Le Plan d'Épargne Retraite (PER) vous permet de préparer votre retraite tout en bénéficiant d'avantages fiscaux immédiats. Sortie en rente ou en capital.",
    coverages: [
      { name: "Déduction Fiscale", description: "Versements déductibles du revenu imposable" },
      { name: "Gestion Libre", description: "Choisissez vos supports d'investissement" },
      { name: "Gestion Pilotée", description: "Sécurisation progressive à l'approche de la retraite" },
      { name: "Sortie Flexible", description: "Capital ou rente viagère au choix" },
      { name: "Déblocage Anticipé", description: "Achat résidence principale possible" },
    ],
  },
};

export default function ProductPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [type]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      const found = res.data.products.find((p) => p.type === type);
      if (found) {
        setProduct({ ...found, ...productDetails[type] });
      }
    } catch (err) {
      console.error("Error fetching product:", err);
    }
  };

  if (!productDetails[type]) return <NotFoundPage />;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const Icon = insuranceIcons[type] || Shield;
  const image = productImages[type];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              {/* Logo animé avec bordure violette */}
              <div className="relative">
                <motion.div
                  className="absolute -inset-1 rounded-xl"
                  style={{
                    background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative bg-white rounded-lg p-1">
                  <img src={JYVIS_LOGO} alt="JYVIS" className="h-8 w-auto" />
                </div>
              </div>
              <span className="font-heading font-bold text-xl text-[#102692]">JYVIS</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)} className="hidden sm:inline-flex" data-testid="product-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={() => navigate(`/devis/${type}`)}
                className="bg-accent hover:bg-accent/90 rounded-full px-6"
                data-testid="get-quote-btn"
              >
                Obtenir un devis
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${image}')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              {product.title}
            </h1>
            <p className="text-xl text-white/80 mb-6">{product.subtitle}</p>
            <p className="text-white/70 leading-relaxed mb-8">{product.description}</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Button
                onClick={() => navigate(`/devis/${type}`)}
                className="bg-accent hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-semibold btn-lift w-full sm:w-auto"
                data-testid="hero-quote-btn"
              >
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <span className="text-white font-semibold">
                {product.price_label || `À partir de ${product.starting_price}€/mois`}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coverages */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-accent uppercase tracking-wider">Nos garanties</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3">
              Ce que comprend votre assurance
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.coverages?.map((coverage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="h-full card-hover" data-testid={`coverage-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <Check className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                      {coverage.name}
                    </h3>
                    <p className="text-slate-600 text-sm">{coverage.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Prêt à souscrire ?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Obtenez votre devis personnalisé en quelques minutes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate(`/devis/${type}`)}
              className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="cta-subscribe-btn"
            >
              Commencer ma souscription
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Être rappelé
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-500 text-sm">
          <LegalBottom />
        </div>
      </footer>
    </div>
  );
}
